import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Eye, EyeOff, Copy, RefreshCw } from "lucide-react";
import toast from "react-hot-toast";
import type { TFunction } from "i18next";

interface CreateAgentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  newAgentEmail: string;
  newAgentName: string;
  newAgentPassword: string;
  setNewAgentEmail: (email: string) => void;
  setNewAgentName: (name: string) => void;
  setNewAgentPassword: (password: string) => void;
  onConfirm: () => void;
  isCreating: boolean;
  generatePassword: () => string;
  t: TFunction<"translation", undefined>;
}

const CreateAgentDialog: React.FC<CreateAgentDialogProps> = ({
  open,
  onOpenChange,
  newAgentEmail,
  newAgentName,
  newAgentPassword,
  setNewAgentEmail,
  setNewAgentName,
  setNewAgentPassword,
  onConfirm,
  isCreating,
  generatePassword,
  t,
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleCopyPassword = () => {
    navigator.clipboard.writeText(newAgentPassword);
    toast.success(t("password_copied") as string);
  };

  const handleGenerateNewPassword = () => {
    setNewAgentPassword(generatePassword());
    toast.success(t("password_generated") as string);
  };

  const handleClose = () => {
    setNewAgentEmail("");
    setNewAgentName("");
    setNewAgentPassword("");
    setShowPassword(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t("create_new_agent")}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="name">{t("name")}</Label>
            <Input
              id="name"
              value={newAgentName}
              onChange={(e) => setNewAgentName(e.target.value)}
              placeholder={t("agent_name") as string}
              className="border border-[#E2E4E9] rounded-lg focus:outline-none focus:ring-1 focus:ring-[#105BDB] focus:border-[#105BDB]"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="email">{t("email")}</Label>
            <Input
              id="email"
              type="email"
              value={newAgentEmail}
              onChange={(e) => setNewAgentEmail(e.target.value)}
              placeholder={t("agent_email") as string}
              className="border border-[#E2E4E9] rounded-lg focus:outline-none focus:ring-1 focus:ring-[#105BDB] focus:border-[#105BDB]"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="password">{t("password")}</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={newAgentPassword}
                  onChange={(e) => setNewAgentPassword(e.target.value)}
                  placeholder={t("password") as string}
                  className="border border-[#E2E4E9] rounded-lg pr-10 focus:outline-none focus:ring-1 focus:ring-[#105BDB] focus:border-[#105BDB]"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-[#6F7D95]" />
                  ) : (
                    <Eye className="h-4 w-4 text-[#6F7D95]" />
                  )}
                </Button>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCopyPassword}
                disabled={!newAgentPassword}
                className="border border-[#E2E4E9] hover:bg-[#F6F7F9]"
              >
                <Copy className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleGenerateNewPassword}
                className="border border-[#E2E4E9] hover:bg-[#F6F7F9]"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={handleClose}
            className="border border-[#E2E4E9] hover:bg-[#F6F7F9]"
          >
            {t("cancel")}
          </Button>
          <Button
            onClick={onConfirm}
            disabled={
              isCreating ||
              !newAgentEmail ||
              !newAgentName ||
              !newAgentPassword ||
              !isValidEmail(newAgentEmail)
            }
            className="bg-[#105BDB] text-white hover:bg-[#0055C4] disabled:bg-[#EDEFF2] disabled:text-[#D5D9E0]"
          >
            {isCreating ? t("creating") : t("create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateAgentDialog;
